// File: src/pages/QuizValeurs.js
// -----------------------------------------------------------------------------
// Page questionnaire : Quelles sont tes valeurs ?
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import IntroScreen from '../components/questionnaires/IntroScreen';
import QuestionScreen from '../components/questionnaires/QuestionScreen';
import ResultsScreen from '../components/questionnaires/ResultsScreen';
import { getUserResult, getUserAnswers, getValuesSurvey, createUserResult, resetValeursSurvey } from '../api/questionnaires';
import { valeursIntroConfig } from '../data/introConfigs';
import { valeursQuestionConfig } from '../data/questionScreenConfigs';
import { valeursResultsConfig } from '../data/resultsScreenConfigs';
import { HistoryContext } from "../context/HistoryContext";

export default function QuizValeurs() {
  const { user } = useContext(AuthContext);
  const [screen, setScreen] = useState('loading');
  const [results, setResults] = useState(null);
  const [existingAnswers, setExistingAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const prenom = user?.firstName || user?.name || 'Toi';
  const { addAction } = useContext(HistoryContext);

  // Vérifier l'état du questionnaire au chargement
  useEffect(() => {
    async function checkStatus() {
      try {
        // Charger en parallèle : résultats, réponses existantes et questions
        const [resultData, answersData, questionsData] = await Promise.all([
          getUserResult(),
          getUserAnswers(),
          getValuesSurvey()
        ]);

        setQuestions(questionsData);
        setExistingAnswers(answersData || []);

        if (resultData) {
          // Questionnaire terminé → afficher les résultats
          setResults(resultData);
          setScreen('results');
        } else if (answersData && answersData.length > 0) {
          // Des réponses existent → reprendre le questionnaire
          setScreen('questionnaire');
        } else {
          // Aucune réponse → afficher l'intro
          setScreen('intro');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut:', error);
        setScreen('intro');
      }
    }
    checkStatus();
  }, []);

  useEffect(() => {
    (async () => {
      if (screen === 'questionnaire' && existingAnswers.length === 0) {
        await addAction({type: "VALUES_SURVEY_STARTED"})
      }
    })();
  }, [screen]);

  const handleComplete = useCallback(async () => {
    // Afficher l'écran résultats avec un loader pendant le premier appel API
    setScreen('results');

    try {
      // Premier appel : créer le résultat (calcul des scores)
      const resultData = await createUserResult();
      setResults(resultData);
      await addAction({type: "VALUES_SURVEY_COMPLETED"})
      // La synthèse sera chargée par ResultsScreen via generateSynthesis()
    } catch (error) {
      console.error('Erreur lors de la création des résultats:', error);
    }
  }, []);

  const handleRestart = useCallback(async () => {
    try {
      await resetValeursSurvey();
      setResults(null);
      setExistingAnswers([]);
      setScreen('intro');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
    }
  }, []);

  if (screen === 'loading') {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex bg-gray-900">
      {screen === 'intro' && (
        <IntroScreen
          prenom={prenom}
          onStart={() => setScreen('questionnaire')}
          config={valeursIntroConfig}
        />
      )}
      {screen === 'questionnaire' && (
        <QuestionScreen
          userId={user?.id}
          gender={user?.gender}
          questions={questions}
          initialAnswers={existingAnswers}
          onBack={() => setScreen('intro')}
          onComplete={handleComplete}
          config={valeursQuestionConfig}
        />
      )}
      {screen === 'results' && (
        <ResultsScreen
          results={results}
          config={valeursResultsConfig}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}